import { Box, Tab, Tabs, Typography, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import './TeacherEarningsViewAll.css';
import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import DisputeIcon from '../../../../../../assets/Admin/EarningIcons/dispute-icon.svg';
import ReceivedIcon from '../../../../../../assets/Admin/EarningIcons/received-icon.svg';
import OneOnOneSessionIcon from '../../../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import { axiosInstance } from '../../../../../../utils/tokenUtils';

function formatText(input) {
  return input
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
}

const EarningHistoryMap = {
  received: {
    class: 'received-view-all-icon-container',
    icon: ReceivedIcon,
    amountClass: 'single-view-all-earning-history-received-amount-text',
  },
  dispute: {
    class: 'dispute-view-all-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-view-all-earning-history-dispute-amount-text',
  },
  withdrawal: {
    class: 'dispute-view-all-icon-container',
    icon: DisputeIcon,
    amountClass: 'single-view-all-earning-history-dispute-amount-text',
  },
};

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: { color: '#279BE2', bgcolor: '#FAFFFB', value: 'Group', icon: GroupSessionIcon },
  prime: { color: '#AD8B00', bgcolor: '#F0EFFF', value: 'Prime', icon: PrimeSessionIcon },
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      aria-label={`${props.label} panel`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TeacherEarningsViewAll = ({ teacherId, conversionRate = 2 }) => {
  const [value, setValue] = useState(0);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  const pageSize = 20;

  const fetchTransactionHistory = useCallback(
    async (pageNumber = 1, resetData = false) => {
      try {
        if (pageNumber === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const response = await axiosInstance.get(
          `/admin/teachers/${teacherId}/transaction-history?page=${pageNumber}&page_size=${pageSize}`
        );

        const newTransactions = response.data.results || [];
        setTotalCount(response.data.count || 0);

        setTransactionHistory(prev =>
          resetData || pageNumber === 1 ? newTransactions : [...prev, ...newTransactions]
        );

        setHasMore(
          newTransactions.length === pageSize &&
            (response.data.offset || 0) + newTransactions.length < (response.data.count || 0)
        );
        setError(null);
      } catch (err) {
        console.error('Error fetching transaction history:', err);
        setError(err.response?.data?.detail || 'Failed to fetch transaction history');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [teacherId, pageSize]
  );

  useEffect(() => {
    if (teacherId) {
      setPage(1);
      fetchTransactionHistory(1, true);
    }
  }, [teacherId, fetchTransactionHistory]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchTransactionHistory(nextPage, false);
    }
  };

  // Calculate koin values using real-time conversion rate
  const calculateKoins = usdAmount => {
    if (!usdAmount || !conversionRate) return 0;

    // Handle both string and number inputs
    let numericAmount;
    if (typeof usdAmount === 'string') {
      numericAmount = parseFloat(usdAmount.replace(/[^0-9.-]+/g, ''));
    } else {
      numericAmount = parseFloat(usdAmount);
    }

    return isNaN(numericAmount) ? 0 : Math.round(numericAmount / conversionRate);
  };

  // Transform API data to match the expected format
  const transformTransactionData = transactions => {
    return transactions.map(transaction => ({
      id: transaction.id,
      type:
        transaction.payment_type === 'withdrawal'
          ? 'withdrawal'
          : ['dispute', 'penalty', 'penalties', 'session_penalty'].includes(
                transaction.payment_type
              )
            ? 'dispute'
            : 'received',
      title:
        transaction.payment_type === 'withdrawal'
          ? 'Withdrawal'
          : `${transaction.session_type} Session`,
      session_type: transaction.session_type,
      payment_type: transaction.payment_type,
      created_at: transaction.created_at, // Keep raw date for proper sorting and grouping
      date: new Date(transaction.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      amount: `${transaction.koins} Koins`,
      koins: calculateKoins(transaction.koins), // Calculate koins using conversion rate
      teacher_name: transaction.teacher_name,
      session_date: transaction.session_date,
      session_duration: transaction.session_duration,
    }));
  };

  // Filter transactions based on selected tab
  const getFilteredTransactions = () => {
    const transformedData = transformTransactionData(transactionHistory);

    switch (value) {
      case 0: // All
        return transformedData;
      case 1: // Received
        return transformedData.filter(item => item.type === 'received');
      case 2: // Other (Disputes)
        return transformedData.filter(item => item.type === 'dispute');
      default:
        return transformedData;
    }
  };

  // Group transactions by month
  const groupTransactionsByDate = transactions => {
    const grouped = {};

    transactions.forEach(transaction => {
      // Use the raw created_at date for proper parsing
      const dateObj = new Date(transaction.created_at);

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date found:', transaction.created_at);
        return; // Skip invalid dates
      }

      // Group by month and year
      const monthKey = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
      });

      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(transaction);
    });

    // Sort months in descending order (most recent first)
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        const dateA = new Date(a + ' 1'); // Add day to make it a valid date
        const dateB = new Date(b + ' 1');
        return dateB - dateA; // Descending order
      })
      .forEach(key => {
        // Sort transactions within each month by date (most recent first)
        grouped[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        sortedGrouped[key] = grouped[key];
      });

    return sortedGrouped;
  };

  // No data message component
  const NoDataMessage = () => (
    <Box className="flex justify-center items-center py-8">
      <Typography variant="body1" color="text.secondary">
        No data available
      </Typography>
    </Box>
  );

  const renderTabContent = () => {
    const filteredTransactions = getFilteredTransactions();
    const groupedTransactions = groupTransactionsByDate(filteredTransactions);
    const hasTransactions = Object.keys(groupedTransactions).length > 0;

    if (!hasTransactions) {
      return <NoDataMessage />;
    }

    return (
      <Box className="flex flex-col gap-4">
        {Object.entries(groupedTransactions).map(([monthYear, transactions]) => (
          <Box key={monthYear} className="flex flex-col gap-4">
            <Typography variant="h6" className="teacher-earnings-month-header-text">
              {monthYear}
            </Typography>
            <Box className="flex flex-col gap-4">
              {transactions.map(item => (
                <Box className="single-view-all-earning-history-container" key={item?.id}>
                  <Box className="flex items-center gap-4">
                    <Box className={EarningHistoryMap[item?.type]?.class}>
                      <img src={EarningHistoryMap[item?.type]?.icon} alt={item?.type} />
                    </Box>
                    <Box className="flex flex-col gap-1">
                      <div className="single-view-all-earning-history-title-text">
                        {formatText(item?.title)}
                      </div>
                      <div className="single-view-all-earning-history-title-text">
                        By {item?.teacher_name}
                      </div>
                    </Box>
                  </Box>
                  <div className="single-view-all-earning-history-payment-type-text">
                    {item?.payment_type ? formatText(item?.payment_type) : '---'}
                  </div>
                  <Box className="flex gap-2">
                    {SessionTypeColor[item?.session_type]?.icon && (
                      <img src={SessionTypeColor[item?.session_type]?.icon} alt="Session icon" />
                    )}
                    <Typography
                      className="session-type-text"
                      sx={{ color: SessionTypeColor[item?.session_type]?.color }}
                    >
                      {item?.session_type ? SessionTypeColor[item?.session_type]?.value : '---'}
                    </Typography>
                  </Box>
                  <div className="date-time-value">{item?.date}</div>
                  <div className={EarningHistoryMap[item?.type]?.amountClass}>
                    {item?.type === 'received' ? '+' : '-'} {item?.amount}
                  </div>
                </Box>
              ))}
            </Box>
          </Box>
        ))}

        {hasMore && (
          <Box className="flex justify-center py-4">
            {loadingMore ? (
              <CircularProgress size={24} />
            ) : (
              <button
                onClick={loadMore}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Load More
              </button>
            )}
          </Box>
        )}

        {!hasMore && transactionHistory.length > 0 && (
          <Box className="flex justify-center py-4">
            <Typography variant="body2" color="text.secondary">
              {totalCount} transaction{totalCount !== 1 ? 's' : ''} total
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: '#0A5EF3' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          padding: '20px',
          textAlign: 'center',
          color: 'error.main',
        }}
      >
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="teacher-session-component"
      className="h-[calc(100vh-260px)] overflow-auto relative bg-[#F9F9F9] rounded-lg"
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            background: '#FFF',
            margin: '15px 15px 0px 15px',
          }}
        >
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="All" sx={{ textTransform: 'none' }} {...a11yProps(0)} />
            <Tab label="Received" sx={{ textTransform: 'none' }} {...a11yProps(1)} />
            <Tab label="Other" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
          </Tabs>
        </Box>

        <CustomTabPanel value={value} index={0}>
          {renderTabContent()}
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          {renderTabContent()}
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2}>
          {renderTabContent()}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default TeacherEarningsViewAll;
